import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { CURRENCIES } from '@/features/crm/deals/constants';
import { resolveEmployeeLabel, searchEmployees } from '@/features/hrm/employees/select';
import { useCreatePayslip } from '../queries';

const schema = z.object({
  employee: z.string().min(1, 'Employee is required'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
  fx_rate_to_base: z.string().refine((v) => Number(v) > 0, 'Must be > 0'),
  // Gross may be left blank — the backend prefills it from the employee's
  // signed staff invoice (ADR-049). Net is derived (gross − deductions).
  gross: z.string().refine((v) => v === '' || Number(v) >= 0, 'Enter an amount'),
  deductions: z.string().refine((v) => v !== '' && Number(v) >= 0, 'Enter an amount'),
  employer_cost: z.string().refine((v) => v !== '' && Number(v) >= 0, 'Enter an amount'),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'employee', 'currency', 'fx_rate_to_base', 'gross', 'deductions', 'employer_cost',
] as const;

export function AddPayslipForm({ runId }: { runId: number }) {
  const create = useCreatePayslip(runId);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee: '',
      currency: 'USD',
      fx_rate_to_base: '1',
      gross: '',
      deductions: '0',
      employer_cost: '0',
    },
  });

  async function handleSubmit(values: FormValues) {
    try {
      const { gross, ...rest } = values;
      await create.mutateAsync({
        ...rest,
        employee: Number(values.employee),
        // Blank gross → omit so the backend prefills from the signed staff
        // invoice (ADR-049); a typed value always wins.
        ...(gross.trim() !== '' ? { gross } : {}),
      });
      form.reset({ ...form.getValues(), employee: '', gross: '' });
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="employee"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Employee</FormLabel>
              <FormControl>
                <EntitySelect
                  queryKey="employee-picker"
                  value={field.value}
                  onChange={field.onChange}
                  search={searchEmployees}
                  resolveLabel={resolveEmployeeLabel}
                  placeholder="Select an employee"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid items-start gap-4 sm:grid-cols-3">
          <FormField
            control={form.control}
            name="gross"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  Gross
                  <InfoHint text="Leave blank to pay the employee’s signed staff invoice for the period." />
                </FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.01" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="deductions"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Deductions</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.01" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="employer_cost"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Employer cost</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.01" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="fx_rate_to_base"
            render={({ field }) => (
              <FormItem>
                <FormLabel>FX → USD</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.00000001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <Button type="submit" size="sm" disabled={create.isPending}>
          <Plus /> {create.isPending ? 'Adding…' : 'Add payslip'}
        </Button>
      </form>
    </Form>
  );
}
